import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { PanelItem } from "@components/panels/PanelItems";
import useServiceHook from "@hooks/useServiceHook";
import PreviewInvoiceService, { TLineItem, TDiscountItem, CouponLabel, TAddon, IPreviewResponse } from '@services/previewInvoice/PreviewInvoiceService';
import LoadingSpinner from "@utils/loadingSpinner";
import InfoBox from "@components/cards/messageBox/InfoBox";
import { countrySpecificCurrency, formatDateToDD_MMM_YYYY, truncateToTwoDecimals } from "@utils/misc/functions";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { OnboardingPersonalState } from './onboardingPersonalReducer';
import { OnboardingAddressState } from './onboardingAddressReducer';
import { OnboardingDriverLicenceState } from './onboardingDriverLicenceReducer';
import { OnboardingEmergencyContactsState } from './onboardingEmergencyContactsReducer';
import { OnboardingAgreementState } from './onboardingAgreementReducer';
import { OnboardingInsuranceState } from './onboardingInsuranceReducer';
import { OnboardingVehicleState } from './onboardingVehicleReducer';
import { Country } from "@utils/constants/localisation";
import { SignatureDocumentService, FlexiOwnV2SubscriptionContractFields, QuoteAddon } from "@services/signatureDocument/SignatureDocumentService";
import Warning from '@components/modal/info/Warning';
import Circular from '@components/spinners/Circular/Circular';

interface InvoicePreviewV2Props {
  navigateTarget: string;
  onboardingPersonalState: OnboardingPersonalState;
  onboardingAddressState: OnboardingAddressState;
  onboardingDriverLicenceState: OnboardingDriverLicenceState;
  onboardingEmergencyContactsState: OnboardingEmergencyContactsState;
  onboardingAgreementState: OnboardingAgreementState;
  onboardingInsuranceState: OnboardingInsuranceState;
  onboardingVehicleState: OnboardingVehicleState;
  country: Country;
  custId: string;
}

function DiscountLabel(label: string) {
  if (typeof label !== 'string')
    return ""
  else if (label.toLowerCase().includes("dynamic"))
    return "Dynamic Pricing"
  else if (label.toLowerCase().includes("ignition"))
    return "Ignition"
  else if (label.toLowerCase().includes("insurance"))
    return "Insurance Beat"
  else
    return label
}

function infoExtraOnEndDate(dateToStr: string) {
  const dateEndOfWeek = new Date();
  dateEndOfWeek.setDate(dateEndOfWeek.getDate() - dateEndOfWeek.getDay() + 7)

  const dateTo = new Date(dateToStr);

  if (dateTo > dateEndOfWeek)
    return " (up to the end of the second subscription week)"
  else
    return ""
}

export default function InvoicePreviewV2({
  navigateTarget,
  onboardingPersonalState,
  onboardingAddressState,
  onboardingDriverLicenceState,
  onboardingEmergencyContactsState,
  onboardingAgreementState,
  onboardingInsuranceState,
  onboardingVehicleState,
  country,
  custId
}: InvoicePreviewV2Props) {
  const navigate = useNavigate();
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const defaultErrorMessage = `We are sorry, an error occurred while processing your request. Click to return home.`;
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);

  // Build coupons object from insurance state
  const coupons = onboardingInsuranceState.hasOtherInsurance === 'true' && onboardingInsuranceState.couponCode
    ? {
        [CouponLabel.ipb]: parseFloat(onboardingInsuranceState.couponCode.match(/[\d.]+/)?.[0] || '0')
      }
    : {};

  const includedAddons: TAddon[] = onboardingAgreementState.quoteAddons
    ?.filter((addon: any) => addon.included === true)
    .map((addon: any): TAddon => ({
      chargebeeItemType: addon.chargebee_item_type,
      chargebeeItemKey: addon.chargebee_item_key,
    })) || [];

  const [loading, error, errMsg, result] = useServiceHook(
    PreviewInvoiceService.getPreviewInvoice,
    [
      country,
      custId,
      onboardingPersonalState.email,
      onboardingAgreementState.planId,
      onboardingAgreementState.subsStartDate,
      coupons,
      includedAddons
    ],
    true,
    [],
  ) as [boolean, boolean, string, IPreviewResponse];

  const estimate = result?.estimate;
  const currencySymbol = countrySpecificCurrency(country);

  // Calculate IPB discount if applicable
  const ipbDiscountAmount = onboardingInsuranceState.hasOtherInsurance === 'true' && onboardingInsuranceState.couponCode
    ? parseInt(onboardingInsuranceState.couponCode.match(/[\d.]+/)?.[0] || '0')
    : 0;

  // Build the signature document payload outside of onSubmit
  const document: FlexiOwnV2SubscriptionContractFields = {
    document_type: 'flexi_own_v2_subscription_contract',

    // Core identifiers
    contact_id: custId,
    vehicle_id: onboardingVehicleState.vehicleId,
    potential_id: onboardingAgreementState.potentialId || null,

    // Plan details
    plan: onboardingVehicleState.plan,
    plan_id: onboardingAgreementState.planId,
    plan_length: onboardingAgreementState.planLen ? String(onboardingAgreementState.planLen) : null,

    // Dates
    start_date: onboardingAgreementState.subsStartDate,

    // Pricing
    pricing_record_id: onboardingAgreementState.pricingRecordId,
    weekly_fee: String(truncateToTwoDecimals(onboardingAgreementState.planWeeklyFee)),
    setup_fee: onboardingAgreementState.planSetupFee || null,

    // Quote addons - send all addons (not just included) so BE can handle C2 N/A fallback logic
    quote_addons: onboardingAgreementState.quoteAddons
      .map((item: any): QuoteAddon => ({
        plan_item_type: item.chargebee_item_type,
        plan_item_key: item.chargebee_item_key,
        plan_component: item.chargebee_component,
        quantity: 1,
        line_item_label_code: item.line_item_label_code,
        price_excluding_gst: item.price_excluding_gst,
        price_including_gst: item.price_including_gst,
        included: item.included,
      })),

    // Vehicle
    odometer: String(onboardingVehicleState.odometer),
    colour: onboardingVehicleState.color,

    // Customer
    email: onboardingPersonalState.email,
    mobile: onboardingPersonalState.mobile || null,
    alternate_name: onboardingPersonalState.alternativeName || null,
    contact_owner_id: onboardingAgreementState.contactOwnerId || null,

    // Customer personal details (provide as overrides for incomplete Zoho data)
    first_name: onboardingPersonalState.firstName || null,
    middle_name: onboardingPersonalState.middleName || null,
    last_name: onboardingPersonalState.lastName || null,
    gender: onboardingPersonalState.gender || null,
    nationality: onboardingPersonalState.nationality || null,
    date_of_birth: onboardingDriverLicenceState.dob || null,
    driver_licence_number: onboardingDriverLicenceState.driverLicenceNumber || null,
    driver_licence_expiry: onboardingDriverLicenceState.licenceExpiry || null,

    // Address details (provide as overrides for incomplete Zoho data)
    street: onboardingAddressState.street || null,
    city: onboardingAddressState.city || null,
    state: onboardingAddressState.state || null,
    postcode: onboardingAddressState.postcode || null,

    // Emergency contacts
    primary_emergency_name: onboardingEmergencyContactsState.primaryName || null,
    primary_emergency_phone: onboardingEmergencyContactsState.primaryPhone || null,
    primary_emergency_address: onboardingEmergencyContactsState.primaryAddress || null,
    primary_emergency_relationship: onboardingEmergencyContactsState.primaryRelationship || null,

    secondary_emergency_name: onboardingEmergencyContactsState.secondaryName || null,
    secondary_emergency_phone: onboardingEmergencyContactsState.secondaryPhone || null,
    secondary_emergency_address: onboardingEmergencyContactsState.secondaryAddress || null,
    secondary_emergency_relationship: onboardingEmergencyContactsState.secondaryRelationship || null,

    // Pricing fields for Zoho Sign template (GB and AU)
    excess_distance_cost: onboardingAgreementState.excessDistanceCost || null,
    loss_and_damage_waiver_value: onboardingAgreementState.lossAndDamageWaiverValue || null,
    a1_total_value: onboardingAgreementState.a1TotalValue || null,
    a2_total_value: onboardingAgreementState.a2TotalValue || null,
    a3_total_value: onboardingAgreementState.a3TotalValue || null,
    total_addons_value: onboardingAgreementState.totalAddonsValue || null,
    reduced_term_length_weeks: onboardingAgreementState.reducedTermLengthWeeks || null,
    term_reduction: onboardingAgreementState.termReduction || null,

    // Used vehicle (pre-loved) addendum fields (GB and AU)
    replacement_tyres: onboardingAgreementState.replacementTyres || null,
    replacement_brake_pads: onboardingAgreementState.replacementBrakePadSet || null,
    brake_or_machined_discs: onboardingAgreementState.replacementOrMachiningOfBrakeDiscs || null,

    // AU-specific fields
    ...(country === Country.AU && {
      hirer_abn: onboardingPersonalState.hirerABN || null,
      toll_tag_number: onboardingPersonalState.tollTagNumber || null,
      toll_tag_account_number: onboardingPersonalState.tollTagAccountNumber || null,
      toll_tag_issuer: onboardingPersonalState.tollTagIssuer || null,
      driver_licence_state: onboardingDriverLicenceState.licenceState || null,
      weekly_distance_allowance: onboardingAgreementState.weeklyDistanceAllowance || null,
      vehicle_driveaway_price: onboardingAgreementState.vehicleDriveawayPrice || null,
      plan_minimum_period: onboardingAgreementState.minimumTerm || null,
      // NSW EV Addendum - only include if plan has NSW incentive data
      nsw_total_incentive_excl_gst: onboardingAgreementState.nswTotalIncentiveExclGST || null,
      nsw_weekly_incentive_excl_gst: onboardingAgreementState.nswWeeklyIncentiveExclGST || null,
    }),

    // Insurance Price Beat - only include if there's an actual discount
    ...(ipbDiscountAmount > 0 && {
      ipb_discount_amount: ipbDiscountAmount,
      ipb_discounted_weekly_subscription: onboardingAgreementState.planWeeklyFee - ipbDiscountAmount
    }),
    insurance_doc_store_id: onboardingInsuranceState.insuranceDocStoreId || null,
  };

  const payload = {
    customer_zoho_contact_id: custId,
    customer_email: onboardingPersonalState.email,
    country: country,
    csr_name: onboardingAgreementState.contactOwner || null,
    csr_email: onboardingAgreementState.contactOwnerEmail || null,
    document: document,
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);  // Intentionally don't set loading false after - only want them to click submit once.

    try {
      const IMSTokenParams = { msalContext, isAuthenticated };

      // 1. Create the signature document
      const createResp = await SignatureDocumentService.createSignatureDocument(
        payload,
        IMSTokenParams
      );

      const documentId = createResp.document_id;
      if (!documentId) throw new Error('No document_id returned');

      // 2. Get the document details (to get the sign_url)
      const detailsResp = await SignatureDocumentService.getSignatureDocumentDetails(
        documentId,
        country,
        IMSTokenParams,
        "customer"
      );

      if (detailsResp && detailsResp.embed_sign_url) {
        // 3. Perform redirect to the embed sign URL
        window.location.replace(detailsResp.embed_sign_url);
      } else {
        throw new Error('No embed_sign_url returned');
      }
    } catch (err: any) {
      // Special case for axios 4xx errors - show user the error message
      if (err && err.response && err.response.status && err.response.status >= 400 && err.response.status < 500) {
        setErrorMessage(err.response.data?.error?.message
          // If no error message from BE - cant show anything more useful than this
          || `Unexpected API Error: (${err.response.status}). Cannot proceed.`);
      } else {
        setErrorMessage(defaultErrorMessage); //Default for a 400 if no message
      }
      setErrorVisible(true);
    }
  };

  return (
    <>
      {errorVisible && (
        <Warning message={errorMessage} additionalInfo="" onClickClose={() => { setErrorVisible(false); navigate('/'); }} />
      )}
      <NavBar pageTitle={"Invoice Preview"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <div style={{ margin: '2.5vh 0' }}>
          <h3>Invoice Preview</h3>
          <p>Review the prorated charges for your subscription.</p>
        </div>

        {loading && <LoadingSpinner testId={"loading-spinner"} />}

        {!loading && estimate && estimate["plan"] && (
          <>
            <div style={{ margin: '2.5vh 0vw', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
              <h4 style={{ marginTop: 0 }}>Prorated Charges</h4>
              <PanelItem
                header={`${onboardingAgreementState.planName} - Prorated Charges`}
                headerTextStyleCfg={{ bold: true }}
                value={`${currencySymbol}${estimate["plan"]["unit_amount"]}`}
                testId={'invoice-preview-plan-amount'}
              />
              {
                estimate["addon"]?.map((item: TLineItem) => (
                  <PanelItem
                    key={item.id}
                    header={(item.description || item.id).length > 50 ?
                      (item.description || item.id).replace(/ - /g, '\n') :
                      (item.description || item.id)}
                    headerTextStyleCfg={{ bold: true }}
                    testId={`invoice-preview-${item.id}`}
                    value={`${currencySymbol}${item.unit_amount}`}
                  />
                ))
              }
              {
                estimate["discounts"]?.map((item: TDiscountItem) => (
                  <PanelItem
                    key={item.id}
                    header={`${DiscountLabel(item.id)}`}
                    headerTextStyleCfg={{ bold: true }}
                    testId={`invoice-preview-${item.id}`}
                    value={`-${currencySymbol}${item.amount}`}
                  />
                ))
              }

              <div style={{ marginTop: '2vh', paddingTop: '2vh', borderTop: '1px solid #E0E0E0' }}>
                <PanelItem
                  header={`Total excl ${estimate.plan.tax_description}`}
                  headerTextStyleCfg={{ bold: true }}
                  value={`${currencySymbol}${estimate.subtotal_ex_tax}`}
                  testId="invoice-preview-plan-amount-exc-tax"
                />
                <PanelItem
                  header={`${estimate.plan.tax_description}`}
                  headerTextStyleCfg={{ bold: true }}
                  value={`${currencySymbol}${estimate.total_tax}`}
                  testId="invoice-preview-total-tax"
                />
                <PanelItem
                  header={`Credits`}
                  headerTextStyleCfg={{ bold: true }}
                  value={`${currencySymbol}${estimate.credits}`}
                  testId="invoice-preview-credits"
                />
                <PanelItem
                  header="Total amount due"
                  headerTextStyleCfg={{ bold: true }}
                  value={`${currencySymbol}${estimate.amount_due}`}
                  testId="invoice-preview-amount-due"
                />
              </div>
            </div>

            <div style={{ margin: '2.5vh 0' }}>
              <InfoBox
                message={`Pro-rated charges apply to the period from ${formatDateToDD_MMM_YYYY(estimate["date_from"])} to ${formatDateToDD_MMM_YYYY(estimate["date_to"])}${infoExtraOnEndDate(estimate["date_to"])}`}
                testId={'invoice-preview-info-box'}
              />
            </div>

            <div style={{ margin: '4vh 0' }}>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                By submitting this form, you confirm that all information provided is accurate and complete, and you accept the charges shown above.
              </p>
            </div>

            {isSubmitting ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Circular />
              </div>
            ) : <SubmitBtn text="Submit and Complete Onboarding" />}
          </>
        )}

        {!loading && error && (
          <div style={{ margin: '2.5vh 0', padding: '20px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '8px' }}>
            <p style={{ color: '#c00', fontWeight: 'bold' }}>Error loading invoice preview</p>
            <p>{errMsg}</p>
          </div>
        )}
      </form>
    </>
  );
}